import BigNumber from 'bignumber.js'
import { BIG_ZERO } from 'config/constants/number'
import { useCallback, useMemo, useRef, useState } from 'react'
import { HunnyToast } from 'utils/toastify'
import { useTranslation } from 'react-i18next'
import { AutoBetConfiguration, AutoBetInfomation } from '../config/autobet'
import { InhouseGameBetResult } from '../config/types'

export const useAutoBet = ({
  onBet,
  onNumberOfBetChanged,
  maxLimit,
  betDuration = 500,
}: {
  onBet: (amount: BigNumber) => Promise<InhouseGameBetResult>
  onNumberOfBetChanged: (value: number) => void
  maxLimit?: number
  betDuration?: number
}) => {
  const infomationRef = useRef<AutoBetInfomation>({})
  const betTimeoutRef = useRef(null)
  const maxLimitWarningToastId = useRef(null)
  const [isRunning, setIsRunning] = useState(false)
  const { t } = useTranslation()

  const [config, setConfig] = useState<AutoBetConfiguration>({
    lossBetConfig: {
      increasePercent: 0,
      isReset: true,
    },
    winBetConfig: {
      increasePercent: 0,
      isReset: true,
    },
    stopLoss: '',
    takeProfit: '',
  })

  const updateConfig = (key: keyof AutoBetConfiguration, value: AutoBetConfiguration[typeof key]) => {
    setConfig({
      ...config,
      [key]: value,
    })
  }

  const subscribe = useCallback(
    (result: { amount: BigNumber; payout: BigNumber }) => {
      const _validateBetAmount = (newBetAmount: BigNumber): BigNumber => {
        const amount = maxLimit ? (newBetAmount.gt(maxLimit) ? new BigNumber(maxLimit) : newBetAmount) : newBetAmount

        if (amount.eq(maxLimit) && !maxLimitWarningToastId.current) {
          maxLimitWarningToastId.current = HunnyToast.show('Your bet amount has reached the maximum limit.', null, {
            autoClose: false,
          })
        } else if (!amount.eq(maxLimit) && maxLimitWarningToastId.current) {
          HunnyToast.dismiss(maxLimitWarningToastId.current)
          maxLimitWarningToastId.current = null
        }
        return maxLimit ? (newBetAmount.gt(maxLimit) ? new BigNumber(maxLimit) : newBetAmount) : newBetAmount
      }

      if (!infomationRef.current || !infomationRef.current.isRunning) return
      const stopLoss = new BigNumber(config.stopLoss || 0)
      const takeProfit = new BigNumber(config.takeProfit || 0)
      infomationRef.current.profit = infomationRef.current.profit.minus(result.amount).plus(result.payout)

      const isOutOfStopLoss =
        infomationRef.current.profit.lt(0) && stopLoss.gt(0) && stopLoss.lte(infomationRef.current.profit.abs())

      const isOutOfTakeProfit =
        infomationRef.current.profit.gt(0) && takeProfit.gt(0) && takeProfit.lte(infomationRef.current.profit.abs())

      if (!infomationRef.current.numberOfBet.isInfinity) {
        infomationRef.current.numberOfBet.current--
        onNumberOfBetChanged(infomationRef.current.numberOfBet.current)
      }

      const isOutOfNumberOfBet =
        !infomationRef.current.numberOfBet.isInfinity && infomationRef.current.numberOfBet.current <= 0
      if (isOutOfStopLoss || isOutOfTakeProfit || isOutOfNumberOfBet) {
        onEnd()
        return
      }
      if (result.payout.gt(result.amount)) {
        if (config.winBetConfig.isReset)
          infomationRef.current.betAmount.value = _validateBetAmount(infomationRef.current.betAmount.origin)
        else {
          const parsedAmount = infomationRef.current.betAmount.value.plus(
            infomationRef.current.betAmount.value.multipliedBy(config.winBetConfig.increasePercent / 100),
          )
          infomationRef.current.betAmount.value = _validateBetAmount(parsedAmount)
        }
      } else if (result.payout.lt(result.amount)) {
        if (config.lossBetConfig.isReset)
          infomationRef.current.betAmount.value = _validateBetAmount(infomationRef.current.betAmount.origin)
        else {
          const parsedAmount = infomationRef.current.betAmount.value.plus(
            infomationRef.current.betAmount.value.multipliedBy(config.lossBetConfig.increasePercent / 100),
          )

          infomationRef.current.betAmount.value = _validateBetAmount(parsedAmount)
        }
      }
      betTimeoutRef.current = setTimeout(async () => {
        if (infomationRef.current?.betAmount) {
          const result = await onBet(infomationRef.current.betAmount.value)
          if (result !== InhouseGameBetResult.Succeed) {
            if (result === InhouseGameBetResult.InsufficientBalance) {
              HunnyToast.error(t('Insufficient balance'))
            } else {
              HunnyToast.error(t('Some thing went wrong'))
            }

            onEnd()
          }
        }
      }, betDuration)
    },
    [onBet, config, betDuration],
  )

  const onStart = (amount: BigNumber, numberOfBet: number) => {
    // Setup data

    infomationRef.current = {
      betAmount: {
        origin: amount,
        value: amount,
      },
      isRunning: true,
      numberOfBet: {
        current: numberOfBet,
        total: numberOfBet,
        isInfinity: !numberOfBet || numberOfBet == 0,
      },
      profit: BIG_ZERO,
    }
    betTimeoutRef.current = null
    setIsRunning(true)

    onBet(amount)
  }

  const onEnd = () => {
    infomationRef.current = {}
    clearTimeout(betTimeoutRef.current)
    if (maxLimitWarningToastId.current) {
      HunnyToast.dismiss(maxLimitWarningToastId.current)
      maxLimitWarningToastId.current = null
    }
    setIsRunning(false)
  }

  return useMemo(
    () => ({
      config,
      updateConfig,
      isRunning,
      onStart,
      onEnd,
      subscribe,
    }),
    [config, isRunning, onBet],
  )
}
